// Form Component to Add or Edit project category

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select, Checkbox } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { TDropdownOptions, TKPI } from "../../../../types/config";
import {
  eDEPARTMENTS,
  eEVALUATION_CRITERIA,
  eResultCode,
} from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  AddEditDesignation,
  GetDesignations,
  GetSpecificKPI,
  AddEditKPI,
  GetSpecificKPICycle,
} from "../../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import utils from "../../../../utils";

type TDrawerProps = {
  groupUniqueId: string;
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {TDrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function KPIForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast
  const [designationList, setDesignationList] = useState<TDropdownOptions[]>([]);

  // Hook to handle on load operations
  useEffect(() => {
    getSpecificKPI(); // Fetch specific group
    getDesignationList(); // Fetch designation list
  }, []);

  // default values initial state
  const defaultValues: TKPI = {
    id: 0,
    name: "",
    description: "",
    isRemark: false
  };

  // Handle on submit event
  const onFinish = async (values: TKPI) => {
    try {
      const payload = {
        data: {
          ...values,
          isCategory: values.isCategory == "1" ? true : false,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditKPI, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const getSpecificKPI = async () => {
    if (props.id) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificKPI, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { data } = response;

          const isCategory = data.category === "BEHAVIORAL" ? "1" : "0";

          form.setFieldsValue({
            ...data,
            isCategory,
          });
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // To fetch the designation list
  const getDesignationList = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1
        },
      };
      setIsLoading(true);
      const response = await post(GetDesignations, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const { data } = response;
        setDesignationList(utils.mapDropdownOptions(data));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16}>
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Project Category`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="name"
                label="Title"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter KPI title"),
                  },
                ]}
              >
                <Input placeholder="Enter KPI title" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="designationIds"
                label="Designation"
                rules={[{ required: true, message: "Select designation" }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={designationList}
                  placeholder="Select designation"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="criteria"
                label="Evaluation Criteria"
                rules={[{ required: true, message: "Select criteria" }]}
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={utils.enumToDropdownOptions(eEVALUATION_CRITERIA)}
                  placeholder="Select criteria"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                label="KPI For (Choose who will evaluate this KPI)"
                name="isCategory"
                rules={[{ required: true, message: "Select KPI For" }]}
              >
                <Select placeholder="Select KPI For">
                  <Select.Option value="1">HR</Select.Option>
                  <Select.Option value="0">Manager</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter KPI description"),
                  },
                ]}
              >
                <TextArea placeholder="Enter KPI description" />
              </Form.Item>
            </Col>
          </Row>


          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="isRemark"
                valuePropName="checked"
              >
                <Checkbox>Is Remark</Checkbox>
              </Form.Item>
            </Col>
          </Row>

        </FormContent>
        <FormFooter className="formFooter">
          <Button
            type="default"
            disabled={isLoading}
            className="btnDimensions"
            onClick={props.onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="btnDimensions"
            onClick={form.submit}
            disabled={isLoading}
          >
            Save
          </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
